/**
 * Abonnés à la lettre d'actualités (email, sans compte) : compteurs, liste,
 * suppression (demande RGPD), envoi du résumé hebdo à la demande.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';
import { useTenant } from '../../../contexts/TenantContext';
import './PublicationsDashboard.css';
import './PublicationEditor.css'; // .btn-back

type Status = 'PENDING' | 'CONFIRMED' | 'UNSUBSCRIBED';

interface Subscriber {
  id: string;
  email: string;
  status: Status;
  source?: string | null;
  customer_id?: string | null;
  created_at: string;
  confirmed_at?: string | null;
  unsubscribed_at?: string | null;
  last_email_at?: string | null;
}

interface ListResponse {
  data: Subscriber[];
  counts: Record<Status, number>;
  email_enabled: boolean;
}

const STATUS_LABEL: Record<Status, { label: string; color: string }> = {
  CONFIRMED: { label: 'Confirmé', color: '#10b981' },
  PENDING: { label: 'En attente de confirmation', color: '#f59e0b' },
  UNSUBSCRIBED: { label: 'Désabonné', color: '#9ca3af' },
};

const SOURCE_LABEL: Record<string, string> = {
  feed: 'Fil des actualités',
  footer: 'Pied de page',
  account: 'Compte client',
  pos: 'Caisse',
  checkout: 'Commande',
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export default function NewsletterSubscribersPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const tenantId = tenant?.id || '';
  const [filter, setFilter] = useState<Status | 'ALL'>('ALL');
  const [result, setResult] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [digestRunning, setDigestRunning] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await api.get('/newsletter/subscribers', {
        params: filter === 'ALL' ? {} : { status: filter },
        headers: { 'X-Tenant-ID': tenantId },
      });
      setResult(data);
    } catch (error: any) {
      alert('Erreur : ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [tenantId, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (subscriber: Subscriber) => {
    if (!confirm(`Supprimer définitivement ${subscriber.email} de la liste ?`)) return;
    try {
      await api.delete(`/newsletter/subscribers/${subscriber.id}`, { headers: { 'X-Tenant-ID': tenantId } });
      await load();
    } catch (error: any) {
      alert('Erreur : ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRunDigest = async () => {
    const confirmed = result?.counts.CONFIRMED || 0;
    if (
      !confirm(
        `Envoyer maintenant le résumé des publications de la semaine (pas encore envoyées) à ${confirmed} abonné(s) confirmé(s) ?`,
      )
    )
      return;
    setDigestRunning(true);
    try {
      const r = await api.post('/newsletter/digest/run', {}, { headers: { 'X-Tenant-ID': tenantId } });
      alert(
        r.skipped
          ? `Rien envoyé : ${r.skipped}.`
          : `Résumé envoyé : ${r.publications} publication(s) à ${r.sent} abonné(s).`,
      );
      await load();
    } catch (error: any) {
      alert('Erreur : ' + (error.response?.data?.message || error.message));
    } finally {
      setDigestRunning(false);
    }
  };

  const counts = result?.counts || { CONFIRMED: 0, PENDING: 0, UNSUBSCRIBED: 0 };

  return (
    <div className="publications-dashboard">
      <div className="dashboard-header">
        <div>
          <button onClick={() => navigate('/publications')} className="btn-back" style={{ marginBottom: 8 }}>
            ← Publications
          </button>
          <h1>Abonnés aux actualités</h1>
          <p className="subtitle">
            Inscrits par email depuis la boutique, sans compte. Chaque email envoyé porte un lien de
            désinscription en un clic.
          </p>
        </div>
        <button onClick={handleRunDigest} className="btn-create" disabled={digestRunning || counts.CONFIRMED === 0}>
          {digestRunning ? 'Envoi…' : '✉️ Envoyer le résumé de la semaine'}
        </button>
      </div>

      {result && !result.email_enabled && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
          }}
        >
          ⚠️ L'envoi d'emails est désactivé sur ce serveur (BREVO_ENABLED=false) : les inscriptions
          sont enregistrées mais aucun email ne part.
        </div>
      )}

      <div className="kpi-grid">
        {(['CONFIRMED', 'PENDING', 'UNSUBSCRIBED'] as Status[]).map((status) => (
          <div
            key={status}
            className="kpi-card"
            onClick={() => setFilter(filter === status ? 'ALL' : status)}
            style={{ cursor: 'pointer', outline: filter === status ? `2px solid ${STATUS_LABEL[status].color}` : 'none' }}
          >
            <div className="kpi-icon">{status === 'CONFIRMED' ? '✅' : status === 'PENDING' ? '⏳' : '🚫'}</div>
            <div className="kpi-value">{counts[status]}</div>
            <div className="kpi-label">{STATUS_LABEL[status].label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : !result || result.data.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>Aucun abonné{filter !== 'ALL' ? ' dans cette catégorie' : ' pour le moment'}</h3>
          <p>Le formulaire d'abonnement est sur le fil des actualités et en pied de page de la boutique.</p>
        </div>
      ) : (
        <div className="publications-table-container overflow-x-auto">
          <table className="publications-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Statut</th>
                <th>Origine</th>
                <th>Inscrit le</th>
                <th>Confirmé le</th>
                <th>Dernier envoi</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {result.data.map((subscriber) => (
                <tr key={subscriber.id}>
                  <td>
                    {subscriber.email}
                    {subscriber.customer_id && (
                      <span title="A aussi un compte client" style={{ marginLeft: 6 }}>
                        👤
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="status-badge" style={{ background: STATUS_LABEL[subscriber.status].color }}>
                      {STATUS_LABEL[subscriber.status].label}
                    </span>
                  </td>
                  <td>{SOURCE_LABEL[subscriber.source || ''] || subscriber.source || '—'}</td>
                  <td className="date-cell">{formatDate(subscriber.created_at)}</td>
                  <td className="date-cell">{formatDate(subscriber.confirmed_at)}</td>
                  <td className="date-cell">{formatDate(subscriber.last_email_at)}</td>
                  <td>
                    <div className="actions-cell">
                      <button
                        onClick={() => handleDelete(subscriber)}
                        className="btn-action btn-delete"
                        title="Supprimer (demande RGPD)"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
